import { useState, useEffect, useRef } from "react";

interface Option {
  value: string;
  label: string;
}
interface AutocompleteProps {
  label: string;
  options: Option[];
  value?: string;
  onChange?: (selected: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  label,
  options,
  value = "",
  onChange,
  placeholder = "Seleccione una opcion",
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [selected, setSelected] = useState<string>(value);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelected(value);
    const label = options.find((o) => o.value === value)?.label || "";
    setInputValue(label);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selected) onChange?.(selected);
  }, [selected]);

  const filteredOptions = options.filter((opt) =>
    inputValue === ""
      ? true
      : opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleSelect = (val: string) => {
    if (disabled) return;
    setSelected(val);
    setInputValue(options.find((o) => o.value === val)?.label || "");
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <label className="block mb-1 text-sm font-medium text-gray-700">
        {label}
      </label>
      <div
        className={`flex items-center h-11 border rounded-lg px-4 pr-11 focus:ring-3 py-2.5 mt-1.5 ${
          disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"
        }`}
        onClick={() => !disabled && setIsOpen(true)}
      >
        <input
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => {
            if (!disabled) {
              setInputValue(e.target.value);
              setIsOpen(true);
            }
          }}
          className="flex-1 outline-none text-sm"
          disabled={disabled}
        />
      </div>

      {isOpen && filteredOptions.length > 0 && !disabled && (
        <div className="absolute z-50 w-full bg-white border mt-1 rounded-lg max-h-60 overflow-y-auto shadow-lg">
          {filteredOptions.map((opt) => (
            <div
              key={opt.value}
              className="cursor-pointer px-4 py-2 hover:bg-primary/10"
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Autocomplete;
